import { isSurfaceColorRole } from '../tokens';
import type { SurfaceColorRole } from '../tokens';
import { createCustomColorField } from './custom-color-field';

/** Options offered by the color dropdown, in display order. */
const SURFACE_ROLE_OPTIONS: readonly { label: string; value: SurfaceColorRole }[] = [
  { label: 'Transparent', value: 'none' },
  { label: 'Surface', value: 'surface' },
  { label: 'Muted', value: 'muted' },
];

/**
 * Puck custom field for a surface (background) color.
 *
 * Offers the theme's semantic surface roles (Transparent, Surface, Muted) plus a custom hex
 * escape hatch — matching the color-row behavior of BackgroundField, but storing only a
 * plain string (role key or hex) rather than a BackgroundValue object. This keeps Heading
 * and Paragraph background values as bare strings and avoids pulling image/size/position
 * controls onto a text component.
 *
 * Value contract: stored value is either a role key (one of `SURFACE_COLOR_ROLES` keys) or
 * a hex string. Resolved at render time via `resolveSurfaceColor`.
 */
export const SurfaceColorField = createCustomColorField({
  options: SURFACE_ROLE_OPTIONS,
  testIdPrefix: 'surface-color',
  cssPrefix: '--dcm-cms-surface-color-field',
  customDefault: '#f5f6f7',
  isNamedOption: isSurfaceColorRole,
  defaultValue: 'none',
});

/** AI schema for a surface color field — role key or hex string. */
export const SURFACE_COLOR_FIELD_AI_SCHEMA = {
  type: 'string',
  description:
    "Either a surface theme role ('none', 'surface', 'muted') or a hex value such as #ff0000. Prefer a role so the color follows merchant branding.",
} as const;
